import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'

import manageStyles from '@/pages/app/Enterprises/components/ManageEnterprises/ManageEnterprises.module.scss'
import sharedStyles from '@/pages/app/Enterprises/components/styles.module.scss'
import enterpriseStyles from '@/pages/app/Enterprises/styles.module.scss'

import { isApiError } from '@/api'
import { DirectorySyncPreview, DirectorySyncUserAction } from '@/api/generated'
import { previewEnterpriseDirectorySync, syncEnterpriseDirectoryUsers } from '@/api/internal'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import { useToast } from '@/components/Toast/ToastContext'
import Tooltip from '@/components/Tooltip'
import EnterpriseDownloads from '@/pages/app/Enterprises/components/EnterpriseDownloads'

interface DirectorySyncControlProps {
  id: string
  name: string
  disabledReason?: string
}

const DiffGroup = ({ title, actions }: { title: string; actions: DirectorySyncUserAction[] }) => {
  if (actions.length === 0) return null
  return (
    <div>
      <strong>
        {title} ({actions.length})
      </strong>
      <ul style={{ maxHeight: 160, overflowY: 'auto', margin: '4px 0 0' }}>
        {actions.map((a) => (
          <li key={a.email}>{a.email}</li>
        ))}
      </ul>
    </div>
  )
}

interface SyncPreviewDialogProps {
  isOpen: boolean
  enterpriseName: string
  preview: DirectorySyncPreview | null
  error: string | null
  isPending: boolean
  onConfirm: () => void
  onDismissError: () => void
  onClose: () => void
}

const SyncPreviewDialog = ({
  isOpen,
  enterpriseName,
  preview,
  error,
  isPending,
  onConfirm,
  onDismissError,
  onClose,
}: SyncPreviewDialogProps) => (
  <DialogWrapper
    isOpen={isOpen}
    title={`Review directory sync — ${enterpriseName}`}
    subtitle={
      preview
        ? `WorkOS directory: ${preview.workos_directory_user_count} users · Database: ${preview.db_user_count} members`
        : undefined
    }
    close={onClose}
  >
    <div className={sharedStyles.dialogContent}>
      {preview && (
        <>
          <DiffGroup title="To add" actions={preview.to_provision} />
          <DiffGroup title="To remove" actions={preview.to_remove} />
          <DiffGroup title="To link" actions={preview.to_link} />
          {preview.to_provision.length === 0 && preview.to_remove.length === 0 && preview.to_link.length === 0 && (
            <div className={sharedStyles.dialogWarningMessage}>No changes — the database already matches WorkOS.</div>
          )}
        </>
      )}
      {error && (
        <div className={manageStyles.errorBox}>
          <span className={manageStyles.errorMessage}>{error}</span>
          <Button title="x" onClick={onDismissError} type="link" className={manageStyles.errorDismiss} />
        </div>
      )}
      <div className={sharedStyles.dialogActions}>
        <Button title="Cancel" onClick={onClose} type="light" />
        <Button title="Confirm sync" onClick={onConfirm} type="dark" isLoading={isPending} isDisabled={isPending} />
      </div>
    </div>
  </DialogWrapper>
)

export const DirectorySyncControl = ({ id, name, disabledReason }: DirectorySyncControlProps) => {
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [preview, setPreview] = useState<DirectorySyncPreview | null>(null)
  const [showPreview, setShowPreview] = useState(false)

  const { updateToast } = useToast()
  const getSyncErrorMessage = (err: unknown, fallback: string) =>
    isApiError(err) ? (err.detail ?? fallback) : fallback

  const previewMutation = useMutation({
    mutationFn: () => previewEnterpriseDirectorySync(id),
    onSuccess: (response) => {
      setDialogError(null)
      setPreview(response.data)
      setShowPreview(true)
    },
    onError: (err) => {
      updateToast(getSyncErrorMessage(err, 'Error computing directory sync preview'), 'error')
    },
  })

  const syncMutation = useMutation({
    mutationFn: (enterpriseId: string) => syncEnterpriseDirectoryUsers(enterpriseId),
    onSuccess: () => {
      setDialogError(null)
      setShowPreview(false)
      setPreview(null)
      updateToast(
        'Directory sync started. Search Sentry logs (flow-backend) for "directory_sync_completed" to see results.',
        'success'
      )
    },
    onError: (err) => {
      const message = getSyncErrorMessage(err, 'Error syncing directory users')
      setDialogError(message)
      updateToast(message, 'error')
    },
  })

  return (
    <>
      <Tooltip text={disabledReason ?? 'Updates our team member list from WorkOS/SCIM'} placement="top" hideArrow>
        <Button
          title="Sync Directory Users"
          onClick={() => previewMutation.mutate()}
          type="light"
          isLoading={previewMutation.isPending}
          isDisabled={previewMutation.isPending || disabledReason !== undefined}
        />
      </Tooltip>

      <SyncPreviewDialog
        isOpen={showPreview}
        enterpriseName={preview?.enterprise_name ?? name}
        preview={preview}
        error={dialogError}
        isPending={syncMutation.isPending}
        onConfirm={() => {
          if (preview) syncMutation.mutate(preview.enterprise_id)
        }}
        onDismissError={() => setDialogError(null)}
        onClose={() => setShowPreview(false)}
      />
    </>
  )
}

const DirectorySyncPage = () => (
  <div className={manageStyles.manageContainer}>
    <h2 className={enterpriseStyles.sectionTitle}>MDM Rollout</h2>
    <EnterpriseDownloads />
  </div>
)

export default DirectorySyncPage
